import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type ThemeMode = 'light' | 'dark'

interface ThemeContextType {
  mode: ThemeMode
  toggleMode: () => void
  setIsForcedLight: (val: boolean) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
  children: ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light')
  const [isForcedLight, setIsForcedLight] = useState(false)

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as ThemeMode | null
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setMode(savedTheme)
    } else {
      setMode('light')
    }
  }, [])

  useEffect(() => {
    // Apply theme to document, but respect the force-light flag
    if (mode === 'dark' && !isForcedLight) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [mode, isForcedLight])

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light'
    setMode(newMode)
    localStorage.setItem('theme', newMode)
  }

  const contextValue: ThemeContextType = {
    mode,
    toggleMode,
    setIsForcedLight
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/**
 * Hook for public pages (login, register, landing) that should always
 * appear in light mode regardless of the user's admin dark mode preference.
 */
export const useForceLightMode = () => {
  const { setIsForcedLight } = useTheme()
  
  useEffect(() => {
    setIsForcedLight(true)
    return () => setIsForcedLight(false)
  }, [setIsForcedLight])
}
